import {
  RecoveryFlow,
  UpdateRecoveryFlowBody,
  UpdateVerificationFlowBody,
} from "@ory/client";
import { UserAuthCard } from "@ory/elements";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { routes } from "@routes";
import { ory } from "@services/authentication";
import { ErrorData, handleError } from "@pages/authentication/error";
import { Authentication } from "@pages/authentication/Authentication";
import { Spinner } from "@thetreep/ui";

export function Recovery() {
  const [flow, setFlow] = useState<RecoveryFlow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = window.location.origin;
  const isSubmittingRef = useRef(false);

  const errorHandler: (error: ErrorData) => unknown = handleError(
    (flowId: string) => getFlow(flowId),
    setFlow,
    routes.LOGIN,
    true,
    navigate,
  );

  const getFlow = async (flowId: string) => {
    try {
      const res = await ory().getRecoveryFlow({ id: flowId });
      setFlow(res.data);
    } catch (e) {
      errorHandler(e as ErrorData);
    }
  };

  const createFlow = async () => {
    try {
      const res = await ory().createBrowserRecoveryFlow({
        ...(returnTo && { returnTo }),
      });

      setSearchParams({ flow: res.data.id });
      setFlow(res.data);
    } catch (error) {
      errorHandler(error as ErrorData);
    }
  };

  const submitFlow = async (body: UpdateVerificationFlowBody) => {
    if (!flow) {
      navigate(routes.LOGIN, { replace: true });
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      const res = await ory().updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: body,
      });
      if ("continue_with" in res.data) {
        for (const cw of res.data.continue_with ?? []) {
          if (cw.action === "show_settings_ui") {
            const search = new URLSearchParams();
            search.set("flow", cw.flow.id);
            navigate(
              {
                pathname: "/settings",
                search: search.toString(),
              },
              { replace: true },
            );
            return;
          }
        }
      }
      setFlow(res.data);
    } catch (error) {
      errorHandler(error as ErrorData);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    const flowId = searchParams.get("flow");
    if (flowId) {
      try {
        getFlow(flowId);
      } catch (e: unknown) {
        console.log("Error getting flow: " + e);
        // oxlint-disable-next-line react-hooks/set-state-in-effect
        createFlow();
      }
      return;
    }
    createFlow();
  }, []);

  if (!flow) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Authentication>
      <UserAuthCard
        flowType="recovery"
        flow={flow}
        className="authCard"
        additionalProps={{
          loginURL: {
            handler: () => {
              navigate(
                {
                  pathname: routes.LOGIN,
                },
                { replace: true },
              );
            },
          },
        }}
        onSubmit={({ body }) => submitFlow(body as UpdateRecoveryFlowBody)}
      />
    </Authentication>
  );
}
